import { useMemo } from 'react';

import { expertRelayComposerPlaceholder } from './composerPlaceholder';

import { PRIMUM_SUPPORT_MESSAGE_CAPTION } from '@/constants/inquiryDisplay';
import type { InquiryParticipant } from '@/hooks/useChat';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import {
  getProfileFormalNameOrFallback,
  getProfileNameOrFallback,
} from '@/util/getProfileNameAndInitials';
import {
  getAnonymousPrimumRoutedRelayExpert,
  getAnonymousPrimumRoutedTitleExpert,
  getInquiryTitleExpert,
} from '@/util/inquiryDisplay';

type UseComposerPlaceholderOptions = {
  inquiry: InquiryV2;
  recipient: InquiryParticipant;
  /** Primum-side conversation in the Ask Anonymously inbox, including No Expert inquiries. */
  isPrimumAskAnonymouslyInboxConversation: boolean;
  /** Primum-side conversation Ask Anonymously forwarded to an expert; the expert is the recipient. */
  isPrimumAskAnonymouslyExpertConversation?: boolean;
  isAnonymousInquiryGroupingEnabled: boolean;
  sendAsExpertRelay: boolean;
};

/**
 * Resolves the composer's template variables (submitter / expert names),
 * the placeholder string, and `showRelaySendPrompt` in one place.
 * All depend on the same set of inquiry and routing inputs so they're
 * co-derived to keep them in sync.
 *
 * `showRelaySendPrompt` gates on the Primum-side Ask Anonymously inbox only — no expert
 * participant required — so the attribution prompt also covers No Expert inquiries, where
 * `title.expert` is absent.
 * The name field pre-fills from `relayExpertParticipant` when available, falling back
 * to "Expert".
 *
 * In Ask Anonymously / Primum-routed relay flows the relay expert is the
 * attributed sender; otherwise we fall back to the inquiry's title expert
 * (or grouped title expert under the grouping flag) and finally to the
 * recipient.
 */
export function useComposerPlaceholder(options: UseComposerPlaceholderOptions) {
  const {
    inquiry,
    recipient,
    isPrimumAskAnonymouslyInboxConversation,
    isPrimumAskAnonymouslyExpertConversation = false,
    isAnonymousInquiryGroupingEnabled,
    sendAsExpertRelay,
  } = options;

  /** In internal relay threads, `recipient` can be the submitter, so resolve the attributed expert from inquiry title/group data. */
  const relayExpertParticipant = useMemo(
    () => getAnonymousPrimumRoutedRelayExpert(inquiry),
    [inquiry]
  );

  const showRelaySendPrompt = isPrimumAskAnonymouslyInboxConversation;

  const effectiveSendAsExpertRelay = showRelaySendPrompt && sendAsExpertRelay;

  const relayExpertAttributedName = getProfileNameOrFallback(relayExpertParticipant, 'Expert');

  const templateVariables = useMemo(() => {
    const submitterName = getProfileFormalNameOrFallback(inquiry.author, 'Doctor');

    const submitterParts = {
      submitterFirst: inquiry.author?.firstName?.trim() || 'Doctor',
      submitterLast: inquiry.author?.lastName?.trim() || 'Doctor',
    };

    // Read the expert off the inquiry, not `recipient`, which is the other participant from the viewer's side.
    if (isPrimumAskAnonymouslyExpertConversation) {
      return {
        submitter: submitterName,
        ...submitterParts,
        expert: getProfileFormalNameOrFallback(inquiry.recipient, 'Expert'),
      };
    }

    if (isPrimumAskAnonymouslyInboxConversation) {
      return {
        submitter: submitterName,
        ...submitterParts,
        expert: getProfileFormalNameOrFallback(relayExpertParticipant, 'Expert'),
      };
    }

    const expertParticipant =
      getInquiryTitleExpert(inquiry, isAnonymousInquiryGroupingEnabled) ??
      getAnonymousPrimumRoutedTitleExpert(inquiry) ??
      recipient;

    return {
      submitter: submitterName,
      ...submitterParts,
      expert: getProfileFormalNameOrFallback(expertParticipant, 'Expert'),
    };
  }, [
    inquiry,
    recipient,
    isAnonymousInquiryGroupingEnabled,
    isPrimumAskAnonymouslyInboxConversation,
    isPrimumAskAnonymouslyExpertConversation,
    relayExpertParticipant,
  ]);

  const composerPlaceholder = expertRelayComposerPlaceholder({
    canSendAsExpertRelay: showRelaySendPrompt,
    sendAsExpertRelay: isPrimumAskAnonymouslyInboxConversation
      ? effectiveSendAsExpertRelay
      : sendAsExpertRelay,
    expertLabel: isPrimumAskAnonymouslyInboxConversation
      ? relayExpertAttributedName
      : templateVariables.expert,
    supportCaption: PRIMUM_SUPPORT_MESSAGE_CAPTION,
    defaultToSupportCaption: isPrimumAskAnonymouslyInboxConversation,
  });

  return {
    templateVariables,
    composerPlaceholder,
    showRelaySendPrompt,
  };
}
